import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { blueGrey, green, grey, orange } from '@mui/material/colors';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { ScreeningCheck, ScreeningCheckOutcome, ScreeningRun } from '../../../interfaces/screening.interface';
import { respondToScreeningCheck, rerunScreening } from '../../../services/screening';
import { updateCompanyCertificationApproval, updateCompanyCertificatioReviewed } from '../../../services/companyCertifications';
import { CHECK_TYPE_LABELS, CHECK_OUTCOME_LABELS, ADMIN_RESPONSE_LABELS } from '../screeningLabels';
import { useAppSelector } from '../../../app/hooks';
import { USER_TYPES } from '../../../services/user';

interface ScreeningRunDetailDialogProps {
  isOpen: boolean;
  run: ScreeningRun | null;
  handleClose: () => void;
  onRunUpdated: (run: ScreeningRun) => void;
}

const OUTCOME_COLORS: Record<ScreeningCheckOutcome, string> = {
  pass: green[600],
  flag: orange[700],
  not_evaluated: grey[600],
  not_applicable: blueGrey[300],
};

function outcomeChip(outcome: ScreeningCheckOutcome) {
  return <Chip size="small" label={CHECK_OUTCOME_LABELS[outcome]} sx={{ backgroundColor: OUTCOME_COLORS[outcome], color: '#fff' }} />;
}

export default function ScreeningRunDetailDialog({ isOpen, run, handleClose, onRunUpdated }: ScreeningRunDetailDialogProps) {
  const { user } = useAppSelector((state) => state.user);
  const userIsAdmin = user?.type === USER_TYPES.TPN_ADMIN;
  const [respondingCheckId, setRespondingCheckId] = useState<number | null>(null);
  const [rerunning, setRerunning] = useState(false);

  if (!run) return null;

  const extraction = run.extraction;

  const handleRespond = async (check: ScreeningCheck, adminResponse: 'accepted' | 'dismissed') => {
    setRespondingCheckId(check.id);
    try {
      const updatedCheck = await respondToScreeningCheck(check.id, adminResponse);
      onRunUpdated({
        ...run,
        checks: run.checks.map((c) => (c.id === check.id ? updatedCheck : c)),
      });
    } catch (error) {
      console.error('Error responding to screening check', error);
    } finally {
      setRespondingCheckId(null);
    }
  };

  const handleRerun = async () => {
    setRerunning(true);
    try {
      await rerunScreening(run.companyCertification);
    } catch (error) {
      console.error('Error dispatching screening re-run', error);
    } finally {
      setRerunning(false);
    }
  };

  const handleApprovedChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    try {
      await updateCompanyCertificationApproval(run.companyCertification, checked);
      onRunUpdated({ ...run, adminApproved: checked });
    } catch (error) {
      console.error('Error updating certification approval', error);
    }
  };

  const handleReviewedChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    try {
      await updateCompanyCertificatioReviewed(run.companyCertification, checked);
      onRunUpdated({ ...run, adminReviewed: checked });
    } catch (error) {
      console.error('Error updating certification reviewed status', error);
    }
  };

  const comparisonRows: { label: string; onCertificate: string; onFile: string }[] = extraction ? [
    {
      label: 'Organization',
      onCertificate: extraction.organizationName || '-',
      onFile: run.companyName || '-',
    },
    {
      label: 'Standard',
      onCertificate: [extraction.standard, extraction.standardVersion].filter(Boolean).join(' '),
      onFile: run.certificationTitle || '-',
    },
    { label: 'Issue Date', onCertificate: extraction.issueDate || '-', onFile: run.recordIssueDate || '-' },
    {
      label: 'Expiration Date',
      onCertificate: extraction.expirationDate || '-',
      onFile: run.recordExpirationDate || '-',
    },
    { label: 'Certificate Number', onCertificate: extraction.certificateNumber || '-', onFile: '-' },
    { label: 'Certification Body', onCertificate: extraction.certificationBody || '-', onFile: '-' },
    { label: 'Accreditation Body', onCertificate: extraction.accreditationBody || '-', onFile: '-' },
    {
      label: 'Sites',
      onCertificate: extraction.sites?.join('; ') || '-',
      onFile: run.knownSites?.join('; ') || '-',
    },
    {
      label: 'Activities / Applications',
      onCertificate: extraction.activities?.join('; ') || '-',
      onFile: run.knownApplications?.join('; ') || '-',
    },
    {
      label: 'Services',
      onCertificate: '-',
      onFile: run.knownServices?.join('; ') || '-',
    },
    { label: 'Document Type', onCertificate: extraction.documentType || '-', onFile: '-' },
  ] : [];

  return (
    <StandardDialog
      isOpen={isOpen}
      handleClose={handleClose}
      title={`Certificate Screening: ${run.companyName} — ${run.certificationTitle}`}
      maxWidth="lg"
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Flags below are a starting point for review, not proof of a fake certificate. Use your own
        judgment and the certification body&apos;s public verification link where available.
      </Alert>

      {run.suspectedInjection && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This run flagged suspected content in the document attempting to influence the automated
          review. Treat the extraction below with extra caution.
        </Alert>
      )}

      {!extraction && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No extraction is available for this run{run.error ? `: ${run.error}` : '.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Switch disabled={!userIsAdmin} checked={run.adminApproved} onChange={handleApprovedChange} />}
          label="Admin Approved"
        />
        <FormControlLabel
          control={<Switch disabled={!userIsAdmin} checked={run.adminReviewed} onChange={handleReviewedChange} />}
          label="Admin Reviewed"
        />
        {run.cbVerificationUrl && (
          <Link href={run.cbVerificationUrl} target="_blank" rel="noreferrer" sx={{ alignSelf: 'center' }}>
            Verify with certification body
          </Link>
        )}
      </Box>

      {extraction && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Certificate vs. Record on File</Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>On Certificate</TableCell>
                <TableCell>On File</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonRows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.onCertificate}</TableCell>
                  <TableCell>{row.onFile}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Checks</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Check</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Rationale</TableCell>
            <TableCell>Admin Response</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {run.checks.map((check) => (
            <TableRow key={check.id}>
              <TableCell>{CHECK_TYPE_LABELS[check.checkType] || check.checkType}</TableCell>
              <TableCell>{outcomeChip(check.outcome)}</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>{check.rationale}</TableCell>
              <TableCell>
                {check.adminResponse ? (
                  <Chip size="small" label={ADMIN_RESPONSE_LABELS[check.adminResponse] || check.adminResponse} />
                ) : check.outcome !== 'flag' ? (
                  '—'
                ) : userIsAdmin ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={respondingCheckId === check.id}
                      onClick={() => handleRespond(check, 'accepted')}
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={respondingCheckId === check.id}
                      onClick={() => handleRespond(check, 'dismissed')}
                    >
                      Dismiss
                    </Button>
                    {respondingCheckId === check.id && <CircularProgress size={20} />}
                  </Box>
                ) : (
                  <Chip size="small" label="Unreviewed" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <StandardDialogActions>
        {userIsAdmin && (
          <Button variant="outlined" onClick={handleRerun} disabled={rerunning}>
            {rerunning ? <CircularProgress size={20} /> : 'Re-run Screening'}
          </Button>
        )}
        <Button variant="contained" onClick={handleClose}>Close</Button>
      </StandardDialogActions>
    </StandardDialog>
  );
}
